// Plans — Demo / MarkovLab / Pro.
// - Demo: 15-day trial, driven by /api/tier/status.
// - MarkovLab: priced at checkout via Stripe (discounts/promos may apply).
// - Pro: waitlist-only — advanced features and labs in development.
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Check, Clock, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { invalidateProCache } from "@/hooks/useIsPro";
import { toast } from "sonner";

const PLATFORM_PITCH =
  "MarkovLab is an options-first income-generation platform — Wheel, CSP, covered calls, and spreads — that also includes portfolio analytics and hedge-system-style tools most generic dashboards skip: Portfolio, Position Management, Wheel Income tracking, Trade Log, Scanner, and Backtest Lab.";

function buildTiers(priceLabel) {
  return [
  {
    id: "demo",
    name: "Demo",
    price: "$0",
    cadence: "15 days",
    subtitle: "Full access — try before you buy.",
    features: [
      "Everything MarkovLab has for 15 days",
      "Scanner, Wheel Income Engine, Portfolio, Position Management",
      "Trade Log & Backtest Lab — plus broker connections",
      "Auto-pauses on day 16 (data preserved)",
    ],
    testid: "billing-card-demo",
  },
  {
    id: "markovlab_monthly",
    name: "MarkovLab",
    price: priceLabel || "Priced at checkout",
    cadence: priceLabel ? "month" : null,
    subtitle: "Options-first income platform with portfolio-grade analytics.",
    features: [
      "Wheel Income Engine, Scanner, and Strike selection",
      "Portfolio, Position Management, Trade Log",
      "Backtest Lab and hedge-system-style risk views",
      "Broker connections (Tradier / Alpaca) included",
      "Cancel anytime — self-service via Stripe",
    ],
    recommended: true,
    testid: "billing-card-markovlab",
  },
  {
    id: "pro_monthly",
    name: "Pro",
    price: null,
    cadence: null,
    subtitle: "Advanced features and labs in development.",
    features: [
      "Everything in MarkovLab",
      "Advanced features and labs in development",
      "Reliability Lab, Hedging Lab, Model Screener (as they ship)",
    ],
    comingSoon: true,
    testid: "billing-card-pro",
  },
  ];
}

export default function BillingPage() {
  const [billing, setBilling] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [waitlisted, setWaitlisted] = useState(false);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      const [{ data: b }, ts] = await Promise.all([
        api.get("/billing/me"),
        api.get("/tier/status").catch(() => ({ data: null })),
      ]);
      setBilling(b);
      setTrialStatus(ts.data);
    } catch {
      setBilling({ tier: "free", is_pro: false });
    }
  };

  useEffect(() => {
    refresh();
    const sessionId = params.get("session_id");
    if (sessionId) {
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data } = await api.get(`/billing/checkout-status/${sessionId}`);
          if (data.payment_status === "paid") {
            invalidateProCache();
            await refresh();
            params.delete("session_id");
            setParams(params, { replace: true });
            toast.success("Payment received — MarkovLab unlocked.");
            return;
          }
          if (data.status === "expired" || attempts >= 6) return;
          setTimeout(poll, 2000);
        } catch { /* ignore */ }
      };
      poll();
    }
  }, []);


  const startCheckout = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        origin: window.location.origin,
        package: "markovlab_monthly",
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Checkout failed");
    } finally { setBusy(false); }
  };

  const openPortal = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin: window.location.origin });
      if (data.portal_url) window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not open the subscription portal — email info@markovlab.app.");
    } finally { setBusy(false); }
  };

  const nextRenewalDate = () => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  };

  const joinWaitlist = async () => {
    try {
      await api.post("/billing/pro-waitlist", { interested_in: "pro_monthly" });
      setWaitlisted(true);
      toast.success("You're on the Pro waitlist. We'll email you when it launches.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join waitlist");
    }
  };

  if (!billing) return <div className="p-6 text-sm" data-testid="billing-loading">Loading plans…</div>;

  const isPro = billing.is_pro;
  const demoMode = !!billing.demo_mode;
  const trialActive = trialStatus?.trial_status === "trial_active" || billing.tier === "trial";
  const trialDays = trialStatus?.days_left;
  const catalogPrice = billing.price_usd != null && Number(billing.price_usd) > 0
    ? `$${Number(billing.price_usd).toFixed(2)} USD`
    : null;
  const TIERS = buildTiers(catalogPrice);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8" data-testid="billing-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
            Plans
          </h1>
          <p className="text-xs mt-1" style={{ color: "var(--hoip-text-secondary)" }}>
            {PLATFORM_PITCH}
          </p>
        </div>
        <button onClick={() => navigate(-1)}
          className="text-xs px-3 py-1 border rounded-sm"
          style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}
          data-testid="billing-back-btn">
          Back
        </button>
      </div>

      {/* Current status strip */}
      <section
        className="border rounded-sm p-4"
        style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
        data-testid="current-plan-card"
      >
        <div className="text-[10px] tracking-widest" style={{ color: "var(--hoip-text-tertiary)" }}>
          CURRENT STATUS
        </div>
        <div className="mt-1 text-xl font-bold flex items-center gap-3 flex-wrap">
          {billing.tier === "legacy_pro" ? "Legacy Pro"
            : isPro ? "MarkovLab"
            : trialActive ? "Demo (Trial)"
            : "No active plan"}
          {isPro && (
            <span className="text-[10px] px-2 py-0.5 rounded-sm"
              style={{ background: "var(--hoip-profit)", color: "#000", fontWeight: 700 }}
              data-testid="pro-badge">MARKOVLAB ACTIVE</span>
          )}
          {!isPro && trialActive && (
            <span className="text-[10px] px-2 py-0.5 rounded-sm flex items-center gap-1"
              style={{ background: "rgba(245,158,11,0.15)", color: "#F59E0B",
                       border: "1px solid rgba(245,158,11,0.4)", fontWeight: 700 }}
              data-testid="trial-badge">
              <Clock className="w-3 h-3" />
              {trialDays != null ? `${trialDays} day${trialDays === 1 ? "" : "s"} left` : "TRIAL"}
            </span>
          )}
        </div>
        {billing.subscription?.started_at && (
          <div className="text-xs mt-2" style={{ color: "var(--hoip-text-secondary)" }}>
            Active since {billing.subscription.started_at.slice(0, 10)}
          </div>
        )}
        {billing.tier === "legacy_pro" && (
          <div className="text-xs mt-2" style={{ color: "var(--hoip-text-secondary)" }}>
            Grandfathered access — no billing required.
          </div>
        )}
      </section>

      {/* Tier cards */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {TIERS.map((tier) => {
          const highlight = tier.recommended;
          return (
            <div key={tier.id}
              className="border p-5 rounded-sm flex flex-col relative"
              style={{
                background: "var(--hoip-card)",
                borderColor: highlight ? "var(--hoip-profit)" : "var(--hoip-border)",
                borderWidth: highlight ? 2 : 1,
                opacity: tier.comingSoon ? 0.9 : 1,
              }}
              data-testid={tier.testid}
            >
              {highlight && (
                <span
                  className="absolute -top-2.5 left-4 text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded"
                  style={{ background: "var(--hoip-profit)", color: "#000",
                           fontFamily: "'JetBrains Mono', monospace" }}
                >
                  Recommended
                </span>
              )}
              {tier.comingSoon && (
                <span
                  className="absolute -top-2.5 left-4 text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded flex items-center gap-1"
                  style={{ background: "rgba(79,195,247,0.15)", color: "#4fc3f7",
                           border: "1px solid rgba(79,195,247,0.4)",
                           fontFamily: "'JetBrains Mono', monospace" }}
                >
                  <Sparkles className="w-2.5 h-2.5" />
                  In development
                </span>
              )}

              <div className="text-[10px] tracking-widest mt-2" style={{ color: "var(--hoip-text-tertiary)" }}>
                {tier.name.toUpperCase()}
              </div>
              <div className="mt-1 font-bold" style={{ fontFamily: "Chivo, sans-serif",
                  fontSize: tier.cadence ? "1.875rem" : "1rem" }}>
                {tier.price}
                {tier.cadence && <span className="text-xs font-normal opacity-60"> / {tier.cadence}</span>}
              </div>
              <p className="text-xs mt-2 mb-4" style={{ color: "var(--hoip-text-secondary)" }}>
                {tier.subtitle}
              </p>
              <ul className="text-xs space-y-2 flex-1 mb-5" style={{ color: "var(--hoip-text-secondary)" }}>
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-3 h-3 mt-0.5 shrink-0"
                      style={{ color: tier.comingSoon ? "#4fc3f7" : "var(--hoip-profit)" }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {/* CTA per tier */}
              {tier.id === "demo" && (
                trialActive ? (
                  <div className="text-xs px-3 py-2 text-center rounded-sm"
                    style={{ background: "var(--hoip-hover)", color: "var(--hoip-text-secondary)" }}
                    data-testid="demo-cta-active">
                    You&apos;re on the trial. {trialDays != null && `${trialDays} day${trialDays === 1 ? "" : "s"} left.`}
                  </div>
                ) : (
                  <div className="text-xs px-3 py-2 text-center rounded-sm"
                    style={{ background: "var(--hoip-hover)", color: "var(--hoip-text-tertiary)" }}
                    data-testid="demo-cta-inactive">
                    {isPro ? "You&apos;re past the trial — enjoy MarkovLab." : "Trial starts on first login after approval."}
                  </div>
                )
              )}

              {tier.id === "markovlab_monthly" && (
                isPro ? (
                  <div className="space-y-2">
                    <div className="text-xs px-3 py-2 text-center rounded-sm"
                      style={{ background: "var(--hoip-hover)", color: "var(--hoip-text-secondary)" }}
                      data-testid="markovlab-cta-active">
                      Currently active.
                    </div>
                    <button onClick={openPortal} disabled={busy}
                      data-testid="manage-subscription-btn"
                      className="w-full py-2 text-xs font-bold tracking-widest rounded-sm border transition-opacity hover:opacity-85 disabled:opacity-50"
                      style={{ borderColor: "var(--hoip-profit)", color: "var(--hoip-profit)" }}>
                      {busy ? "OPENING…" : "MANAGE SUBSCRIPTION"}
                    </button>
                    <p className="text-[9px] text-center" style={{ color: "var(--hoip-text-tertiary)" }}>
                      Cancel or update your card anytime via the Stripe customer portal.
                    </p>
                  </div>
                ) : demoMode ? (
                  <a href="mailto:info@markovlab.app?subject=MarkovLab%20early%20access"
                    data-testid="markovlab-cta-demo-mode"
                    className="w-full py-2.5 text-xs font-bold tracking-widest rounded-sm text-center block transition-opacity hover:opacity-85"
                    style={{ background: "var(--hoip-profit)", color: "#000" }}>
                    CONTACT US FOR EARLY ACCESS
                  </a>
                ) : (
                  <>
                    {/* FIX-04 — checkout sequence disclosures: amount + USD +
                        cadence + renewal date + merchant country + descriptor. */}
                    <div className="text-[9px] leading-relaxed mb-2 p-2 border rounded-sm space-y-1"
                      style={{ borderColor: "var(--hoip-divider)", color: "var(--hoip-text-tertiary)" }}
                      data-testid="billing-checkout-disclosures">
                      <div><strong style={{ color: "var(--hoip-text-secondary)" }}>{catalogPrice ? `${catalogPrice} / month` : "Price shown at checkout"}</strong> — renews monthly on your subscription date (first renewal {nextRenewalDate()}). Promotional pricing may apply. Cancel anytime.</div>
                      <div data-testid="merchant-country-line">Sold by Concrete Control LLC (dba MarkovLab), United States.</div>
                      <div data-testid="statement-descriptor-note">Your card statement will show <strong>MARKOVLAB</strong>.</div>
                      <div>
                        <a href="/legal/terms" target="_blank" rel="noreferrer" className="underline">Terms</a>{" · "}
                        <a href="/legal/privacy" target="_blank" rel="noreferrer" className="underline">Privacy</a>{" · "}
                        <a href="/legal/refund" target="_blank" rel="noreferrer" className="underline">Refund &amp; Cancellation Policy</a>
                      </div>
                    </div>
                    <button onClick={startCheckout} disabled={busy}
                      data-testid="markovlab-subscribe-btn"
                      className="w-full py-2.5 text-xs font-bold tracking-widest rounded-sm transition-opacity hover:opacity-85 disabled:opacity-50"
                      style={{ background: "var(--hoip-profit)", color: "#000" }}>
                      {busy ? "OPENING CHECKOUT…" : (catalogPrice ? `SUBSCRIBE — ${catalogPrice} / MONTH` : "SUBSCRIBE")}
                    </button>
                  </>
                )
              )}

              {tier.id === "pro_monthly" && (
                <>
                  <p className="text-[9px] leading-relaxed mb-2" style={{ color: "var(--hoip-text-tertiary)" }}
                    data-testid="pro-dev-note">
                    Pro is still in development. Nothing will be charged — join the waitlist to be
                    notified when it&apos;s ready.
                  </p>
                  {waitlisted ? (
                    <div className="text-xs px-3 py-2 text-center rounded-sm"
                      style={{ background: "rgba(79,195,247,0.1)", color: "#4fc3f7" }}
                      data-testid="pro-waitlist-joined">
                      You&apos;re on the list — we&apos;ll email you when it launches.
                    </div>
                  ) : (
                    <button onClick={joinWaitlist}
                      data-testid="pro-waitlist-btn"
                      className="w-full py-2.5 text-xs font-bold tracking-widest rounded-sm border transition-colors hover:bg-cyan-500/10"
                      style={{ borderColor: "#4fc3f7", color: "#4fc3f7" }}>
                      JOIN WAITLIST
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })}
      </section>

      <p className="text-[10px] max-w-3xl" style={{ color: "var(--hoip-text-tertiary)" }}>
        MarkovLab is analytical software for self-directed investors. It does not provide
        investment advice or execute trades. Options involve substantial risk and are not
        suitable for all investors. Subscriptions are billed in USD via Stripe by Concrete
        Control LLC (dba MarkovLab), United States; cancel anytime via the Stripe customer
        portal. See our <a href="/legal/refund" className="underline">Refund &amp; Cancellation Policy</a>.
      </p>
    </div>
  );
}
